/*
The MIT License (MIT)

Copyright 2018-present Chen Fengyuan

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.

thanks to and based on: https://github.com/fengyuanchen/vue-qrcode
*/

import { defineComponent, h } from "vue";
import { toCanvas, toDataURL, toString } from "qrcode";

const EVENT_READY = "ready";

export default defineComponent({
  name: "VueQrcode",

  props: {
    /**
     * The value of the QR code.
     */
    value: {
      type: String,
      default: undefined,
    },

    /**
     * The options for the QR code generator.
     * {@link https://github.com/soldair/node-qrcode#qr-code-options}
     */
    options: {
      type: Object,
      default: undefined,
    },

    /**
     * The tag name of the component's root element.
     */
    tag: {
      type: String,
      default: "canvas",
    },
  },

  emits: [EVENT_READY],

  watch: {
    $props: {
      deep: true,
      immediate: true,

      /**
       * Update the QR code when props changed.
       */
      handler() {
        if (this.$el) {
          this.generate();
        }
      },
    },
  },

  mounted() {
    this.generate();
  },

  methods: {
    /**
     * Generate QR code.
     */
    generate() {
      const options = this.options || {};
      const value = String(this.value);
      const done = () => {
        this.$emit(EVENT_READY, this.$el);
      };

      switch (this.tag) {
        case "canvas":
          toCanvas(this.$el, value, options, (error) => {
            if (error) {
              throw error;
            }

            done();
          });
          break;

        case "img":
          toDataURL(value, options, (error, url) => {
            if (error) {
              throw error;
            }

            this.$el.src = url;
            this.$el.onload = done;
          });
          break;

        case "svg":
          toString(value, options, (error, string) => {
            if (error) {
              throw error;
            }

            const div = document.createElement("div");

            div.innerHTML = string;

            const svg = div.querySelector("svg");

            if (svg) {
              const { attributes, childNodes } = svg;

              Object.keys(attributes).forEach((key) => {
                const attribute = attributes[Number(key)];

                this.$el.setAttribute(attribute.name, attribute.value);
              });
              Object.keys(childNodes).forEach((key) => {
                const childNode = childNodes[Number(key)];

                this.$el.appendChild(childNode.cloneNode(true));
              });
              done();
            }
          });
          break;

        default:
      }
    },
  },

  render() {
    return h(this.tag, this.$slots.default);
  },
});
